// src/components/sourcex/SourceDocsVersionNotice.js
//
// SourceX's built source documents (SectionEditor), flag PROTOCOL_COMPARE_ENABLED
// (decided 2026-10-23): when a newer protocol version was added after these
// documents were built, EACH VISIT asks to be checked against it. Opening a visit
// nobody has checked shows a pop-up every time, until someone says they've
// checked that visit (recorded per visit: who and when, shown to everyone) or
// snoozes it until tomorrow (for themselves, on this company and site only).
// It asks for EDITS, never a rebuild: the user doesn't want source documents
// regenerated. This is the only place in SourceX that warns about a new
// protocol, by decision.

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FileWarning, Loader2, CheckCircle2 } from "lucide-react";
import { auth } from "../../firebase";
import { PROTOCOL_COMPARE_ENABLED } from "../../config/featureFlags";
import { getActiveTenantId, getActiveSiteId } from "../../services/activeTenant";
import { listenVersions, listenVisitChecks, checkVisit, logProtocolEvent } from "../../services/protocolVersionService";
import { sourceDocsNotice } from "../../services/protocolVersions";

const fmt = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { dateStyle: "medium" }) : "");
const toMs = (t) => (t && typeof t.toMillis === "function" ? t.toMillis() : Number(t) || 0);

// Snooze "until tomorrow" for this person, company and site (never shared).
const snoozeKey = (studyKey, version, visit) =>
  `regx:protocolVisitSnooze:${auth.currentUser?.uid || "anon"}:${getActiveTenantId()}:${getActiveSiteId()}:${studyKey}:v${version}:${visit}`;
const tomorrow = () => {
  const d = new Date();
  d.setHours(24, 0, 0, 0);
  return d.getTime();
};
const readSnooze = (key) => {
  try {
    return Number(window.localStorage.getItem(key)) || 0;
  } catch (e) {
    return 0;
  }
};
const writeSnooze = (key, until) => {
  try {
    window.localStorage.setItem(key, String(until));
  } catch (e) {
    /* private window: the snooze lasts for this page visit only */
  }
};

/**
 * @param {{studyKey: string, template: object, visit: string|null, visits?: string[], paused?: boolean}} props
 *   visit: the visit open in the editor; visits: every visit in these documents.
 *   paused: show nothing for now (the SourceX tour is being asked or running,
 *   2026-10-29); it shows as usual once it's false again.
 */
const SourceDocsVersionNotice = ({ studyKey, template, visit, visits = [], paused = false }) => {
  const [versions, setVersions] = useState([]);
  const [checks, setChecks] = useState([]);
  const [snoozedNow, setSnoozedNow] = useState({}); // visit -> true (this page visit)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!PROTOCOL_COMPARE_ENABLED || !studyKey) return undefined;
    return listenVersions(studyKey, setVersions, () => setVersions([]));
  }, [studyKey]);

  // When these documents were made: built (assembledAt), or made live (promotedAt).
  // A hand-built template with content and no date counts as older.
  const builtAt = template
    ? template.assembledAt || template.promotedAt || template.createdAt || ((template.sections || []).length ? 1 : null)
    : null;
  const notice = PROTOCOL_COMPARE_ENABLED ? sourceDocsNotice({ versions, builtAt }) : null;
  const version = notice ? notice.version : null;

  useEffect(() => {
    if (!studyKey || version == null) return undefined;
    return listenVisitChecks(studyKey, version, setChecks, () => setChecks([]));
  }, [studyKey, version]);

  const byVisit = useMemo(() => new Map(checks.map((c) => [c.visit, c])), [checks]);
  if (!notice || !visit || paused) return null;

  const done = byVisit.get(visit);
  const total = [...new Set(visits.filter(Boolean))].length;
  const checkedCount = [...new Set(visits)].filter((v) => byVisit.has(v)).length;
  const key = snoozeKey(studyKey, version, visit);
  const snoozed = snoozedNow[visit] || readSnooze(key) > Date.now();

  // Checked: a quiet note for everyone, so they know who did it.
  if (done) {
    return createPortal(
      <div role="status" className="protocol-version-pill fixed bottom-4 left-4 z-[70] max-w-sm rounded-xl border border-emerald-400/30 bg-emerald-500/15 px-3 py-2 text-[12px] text-emerald-100 shadow-lg flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          {visit} was checked against Version {version}
          {done.checkedBy ? ` by ${done.checkedBy}` : ""}
          {toMs(done.checkedAt) ? ` on ${fmt(toMs(done.checkedAt))}` : ""}.
          {total ? ` ${checkedCount} of ${total} visits checked.` : ""}
        </span>
      </div>,
      document.body
    );
  }
  if (snoozed) return null;

  const markChecked = async () => {
    setBusy(true);
    setError("");
    try {
      await checkVisit(studyKey, version, visit);
    } catch (e) {
      setError(e.message || "Couldn't save that. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  const snooze = () => {
    writeSnooze(key, tomorrow());
    setSnoozedNow((m) => ({ ...m, [visit]: true }));
    logProtocolEvent(studyKey, { type: "visit_snoozed", version, visit });
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-[Manrope]">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="A new protocol was uploaded"
        className="protocol-version-dialog w-full max-w-md rounded-2xl border border-white/10 bg-[#0c0826]/95 p-5 text-slate-100 shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)]"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="protocol-version-icon w-9 h-9 rounded-xl bg-purple-500/15 border border-white/15 flex items-center justify-center shrink-0">
            <FileWarning className="w-4 h-4 text-purple-300" />
          </div>
          <h2 className="text-base font-semibold text-white">A new protocol was uploaded</h2>
        </div>
        <p className="text-sm text-slate-300">
          Version {version} of this study&apos;s protocol{notice.fileName ? ` (${notice.fileName})` : ""} was uploaded
          {notice.addedAt ? ` on ${fmt(notice.addedAt)}` : ""}. These source documents were made from an earlier version.
        </p>
        <p className="text-sm text-slate-300 mt-2">
          Please review <strong className="text-white">{visit}</strong> and make any updates by hand if needed. You don&apos;t need to
          regenerate the documents.
        </p>
        {total > 0 && <p className="text-[12px] text-slate-400 mt-2">{checkedCount} of {total} visits checked so far.</p>}
        {error && <div role="alert" className="mt-3 text-xs text-rose-300">{error}</div>}
        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button type="button" onClick={snooze} disabled={busy} className="protocol-version-soft-btn px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-200">
            Snooze until tomorrow
          </button>
          <button
            type="button"
            onClick={markChecked}
            disabled={busy}
            className="protocol-version-solid-btn inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-sm font-semibold text-white ring-1 ring-white/20"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} I&apos;ve checked {visit}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SourceDocsVersionNotice;
